/* Monday Morning Briefing (#16) — wekelijkse digest van de agent-vloot.
 *
 * Toont de meest recente briefing: LLM-intro, weekcijfers, top-signalen,
 * aanbevolen acties en de stilgevallen deals (#17 stale-deal detector) met
 * één-klik reactivatie (concept verschijnt in de Upcoming-inbox).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "./Dashboard";
import {
    Sparkles, Loader2, RefreshCw, ArrowRight, Radar, MessageSquare,
    CheckCircle2, RotateCcw, TrendingUp, Clock,
} from "lucide-react";

const ACTION_ICONS = { approve: CheckCircle2, stale: RotateCcw, signals: Radar, replies: MessageSquare, hunt: TrendingUp };

export default function Briefing() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [briefing, setBriefing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [reactivating, setReactivating] = useState(null);
    const [reactivated, setReactivated] = useState(new Set());

    const load = async () => {
        try {
            const r = await api.get("/briefing/latest");
            setBriefing(r.data.briefing);
        } catch { /* leeg */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            const r = await api.post("/briefing/generate");
            setBriefing(r.data.briefing);
            toast.success(t("briefing.generated_ok"));
        } catch (e) { toast.error(e?.response?.data?.detail || t("briefing.generate_failed")); }
        finally { setGenerating(false); }
    };

    const reactivate = async (item) => {
        setReactivating(item.id);
        try {
            await api.post("/stale/reactivate", { kind: item.kind, id: item.id });
            setReactivated((prev) => new Set(prev).add(item.id));
            toast.success(t("briefing.reactivated_ok", { name: item.name }));
        } catch (e) { toast.error(e?.response?.data?.detail || t("briefing.reactivate_failed")); }
        finally { setReactivating(null); }
    };

    const stats = briefing?.stats || {};
    const tiles = [
        { label: t("briefing.tile_signals"), value: stats.signals_new ?? 0, testid: "brief-tile-signals" },
        { label: t("briefing.tile_sent"), value: stats.outreach_sent ?? 0, testid: "brief-tile-sent" },
        { label: t("briefing.tile_replies"), value: stats.replies ?? 0, testid: "brief-tile-replies" },
        { label: t("briefing.tile_pending"), value: stats.pending_approvals ?? 0, testid: "brief-tile-pending" },
        { label: t("briefing.tile_stale"), value: (briefing?.stale || []).length, testid: "brief-tile-stale" },
    ];

    return (
        <PageHeader title={t("briefing.title")} subtitle={t("briefing.subtitle")}>
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]" data-testid="briefing-week">
                    {briefing ? t("briefing.week_of", { date: briefing.week_start }) : ""}
                </div>
                <button onClick={generate} disabled={generating} data-testid="briefing-generate"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-amber)] text-black text-sm font-semibold hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                    {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {generating ? t("briefing.generating") : t("briefing.generate_btn")}
                </button>
            </div>

            {loading ? (
                <div className="grid place-items-center py-20 text-[var(--jr-text-3)]"><Loader2 className="animate-spin" /></div>
            ) : !briefing ? (
                <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-10 text-center" data-testid="briefing-empty">
                    <Sparkles size={28} className="mx-auto text-[var(--jr-amber)] mb-3" />
                    <p className="text-sm text-[var(--jr-text-2)]">{t("briefing.empty")}</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Intro */}
                    <div className="rounded-xl border border-[var(--jr-amber)]/30 bg-[var(--jr-amber)]/8 p-5" data-testid="briefing-intro">
                        <div className="flex items-center gap-2 mb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-amber)]">
                            <Sparkles size={12} /> {t("briefing.intro_label")}
                        </div>
                        <p className="text-base text-white leading-relaxed">{briefing.intro}</p>
                    </div>

                    {/* Stat-tegels */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {tiles.map((tl) => (
                            <div key={tl.testid} data-testid={tl.testid}
                                className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4">
                                <div className="text-2xl font-semibold text-white">{tl.value}</div>
                                <div className="text-[11px] text-[var(--jr-text-3)] mt-1">{tl.label}</div>
                            </div>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-2 gap-5">
                        {/* Aanbevolen acties */}
                        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-5" data-testid="briefing-actions">
                            <h3 className="text-sm font-semibold text-white mb-3">{t("briefing.actions_label")}</h3>
                            <div className="space-y-2">
                                {(briefing.actions || []).map((a, i) => {
                                    const Icon = ACTION_ICONS[a.kind] || ArrowRight;
                                    return (
                                        <button key={i} onClick={() => a.link && navigate(a.link)}
                                            data-testid={`briefing-action-${i}`}
                                            className="w-full flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5 text-left hover:border-[var(--jr-amber)]/50 group">
                                            <Icon size={15} className="text-[var(--jr-amber)] shrink-0" />
                                            <span className="text-sm text-[var(--jr-text-2)] group-hover:text-white">{a.text}</span>
                                            <ArrowRight size={13} className="ml-auto text-[var(--jr-text-3)] group-hover:text-[var(--jr-amber)]" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Top signalen */}
                        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-5" data-testid="briefing-top-signals">
                            <h3 className="text-sm font-semibold text-white mb-3">{t("briefing.top_signals_label")}</h3>
                            {(stats.top_signals || []).length === 0 ? (
                                <p className="text-xs text-[var(--jr-text-3)]">{t("briefing.no_signals")}</p>
                            ) : (
                                <div className="space-y-2">
                                    {stats.top_signals.map((s, i) => (
                                        <div key={i} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5">
                                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full border border-[var(--jr-brand)]/40 text-[var(--jr-brand)]">{s.score}</span>
                                            <div className="min-w-0">
                                                <div className="text-sm text-white truncate">{s.company_name}</div>
                                                <div className="text-[11px] text-[var(--jr-text-3)] truncate">{s.signal_title}</div>
                                            </div>
                                            <span className="ml-auto text-[9px] font-mono uppercase text-[var(--jr-text-3)]">{s.signal_type}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stale deals (#17) */}
                    <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-5" data-testid="briefing-stale">
                        <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
                            <Clock size={14} className="text-[#f87171]" /> {t("briefing.stale_label")}
                        </h3>
                        <p className="text-[11px] text-[var(--jr-text-3)] mb-3">{t("briefing.stale_hint")}</p>
                        {(briefing.stale || []).length === 0 ? (
                            <p className="text-xs text-[#22c55e]">{t("briefing.no_stale")}</p>
                        ) : (
                            <div className="space-y-2">
                                {briefing.stale.map((d) => (
                                    <div key={d.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2.5"
                                        data-testid={`stale-item-${d.id}`}>
                                        <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full border ${d.kind === "lead"
                                            ? "text-[var(--jr-brand)] border-[var(--jr-brand)]/40" : "text-[#a78bfa] border-[#a78bfa]/40"}`}>
                                            {d.kind === "lead" ? "Sales" : "Talent"}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="text-sm text-white truncate">{d.name}{d.company ? ` · ${d.company}` : ""}</div>
                                            <div className="text-[11px] text-[var(--jr-text-3)]">{d.stage} · {t("briefing.days_quiet", { n: d.days_stale })}</div>
                                        </div>
                                        <button onClick={() => reactivate(d)}
                                            disabled={reactivating === d.id || reactivated.has(d.id)}
                                            data-testid={`stale-reactivate-${d.id}`}
                                            className={`ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium ${reactivated.has(d.id)
                                                ? "border border-[#22c55e]/40 text-[#22c55e] cursor-default"
                                                : "border border-[var(--jr-amber)]/50 text-[var(--jr-amber)] hover:bg-[var(--jr-amber)]/10"} disabled:opacity-60`}>
                                            {reactivating === d.id ? <Loader2 size={12} className="animate-spin" />
                                                : reactivated.has(d.id) ? <CheckCircle2 size={12} /> : <RotateCcw size={12} />}
                                            {reactivated.has(d.id) ? t("briefing.reactivated_short") : t("briefing.reactivate_btn")}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </PageHeader>
    );
}
